import { Typography, IconButton } from "@mui/material";
import CampaignIcon from "@mui/icons-material/Campaign";
import getLocale from "../../../common/helpers/getLocale";
import { useTranslation } from "react-i18next";
import { AnnouncementItem, Severity } from "./types";
import { FlexVerticalAligned } from "../../../common/components/styled/styled";
import { CustomButton } from "../../../common/components/functional/customButton/CustomButton";
import Close from "@mui/icons-material/Close";
import useNotification from "../../../hooks/useNotification";
import { getBannerColor, markReadAnnouncement } from "./helpers";
import { removeAnnouncement } from "./announcementSlice";
import { useAppDispatch } from "../../../app/hooks";
import { useLayoutEffect, useRef, useState } from "react";

type Props = {
  announcement: AnnouncementItem;
};

export const AnnouncementBanner = ({ announcement }: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation("settings");
  const { sendNotification } = useNotification();
  const language = getLocale().code.toUpperCase();
  const textContainerRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const text =
    announcement.messages.find((msg) => msg.languageCode === language)?.text ||
    "";

  useLayoutEffect(() => {
    const el = textContainerRef.current;
    if (!el) return;

    let cancelled = false;
    const updateOverflow = () => {
      if (cancelled) return;
      setIsOverflowing(el.scrollWidth > el.clientWidth + 1);
    };

    updateOverflow();
    const observer = new ResizeObserver(updateOverflow);
    observer.observe(el);
    void document.fonts?.ready.then(updateOverflow);

    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [text]);

  const openPreview = () => {
    sendNotification(t("settings:announcement"), text);
  };

  const markAsRead = () => {
    markReadAnnouncement(announcement.id);
    dispatch(removeAnnouncement(announcement.id));
  };

  return (
    <FlexVerticalAligned
      key={announcement.id}
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        overflow: "hidden",
        backgroundColor: getBannerColor(announcement.severity),
        px: 2,
        py: 1,
        color: "black",
        justifyContent: "space-between",
      }}
    >
      <FlexVerticalAligned sx={{ minWidth: 0, flex: 1, overflow: "hidden" }}>
        <CampaignIcon
          sx={{
            mr: 1,
            flexShrink: 0,
            color: (theme) => theme.palette.primary.main,
          }}
        />
        <Typography
          color={(theme) => theme.palette.primary.main}
          fontWeight={700}
          mr={1.5}
          flexShrink={0}
        >
          {t("announcement")}
        </Typography>
        <Typography
          ref={textContainerRef}
          component="div"
          noWrap
          sx={{
            minWidth: 0,
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {text}
        </Typography>
        {isOverflowing && (
          <CustomButton
            variant="text"
            sx={{
              height: "24px",
              width: "120px",
              ml: 1,
              flexShrink: 0,
              borderRadius: "16px",
            }}
            onClick={openPreview}
          >
            {t("common:readMore")}
          </CustomButton>
        )}
      </FlexVerticalAligned>
      {announcement.severity !== Severity.Critical && (
        <IconButton
          onClick={markAsRead}
          sx={{
            width: "24px",
            height: "24px",
            ml: 1,
            flexShrink: 0,
            color: (theme) => theme.palette.primary.main,
          }}
        >
          <Close sx={{ width: "20px" }} />
        </IconButton>
      )}
    </FlexVerticalAligned>
  );
};
